/** The pen and the eraser: lines drawn by hand, each one item, rubbed out in one undo. */
import { createSignal } from "solid-js";
import { toast } from "../../shared/toast";
import { INK_TOLERANCE_PX, PEN_COLOURS, PEN_WIDTHS } from "./constants";
import type { BoardCtx } from "./context";
import { frameAround, inkOf, rounded, topZ } from "./geometry";
import { drawingKind } from "./tools";
import type { BoardPart } from "./types";

/** The pen's members of the board service. */
export function inkPart(ctx: BoardCtx): BoardPart<"pen" | "setPen" | "draw" | "erase"> {
  const { actions, kinds } = ctx.deps;
  const [pen, setPen] = createSignal<{ colour: string; width: number }>({ colour: PEN_COLOURS[0], width: PEN_WIDTHS[1] });
  return {
    pen,
    setPen(next) {
      setPen({ ...pen(), ...next });
    },
    async draw(points) {
      const { colour, width } = pen();
      const ink = inkOf(points, width, INK_TOLERANCE_PX / ctx.camera().k);
      if (!ink) return null;
      const kind = drawingKind(ctx);
      if (!kind) return void toast("Turn on a plugin that brings drawings (Art) to draw by hand.", "error"), null;
      const place = rounded({ ...ink.place, z: topZ(ctx.content()) + 1 });
      const frame = frameAround(ctx.content(), place);
      const ids = await ctx.addAll([{ name: kinds.board(kind).words || "Drawing", kind, categories: [], board: place, style: { path: ink.path, stroke: colour, width: String(width) }, ...(frame ? { group: frame } : {}) }]);
      return ids?.[0] ?? null;
    },
    async erase(ids) {
      const drawn = ids.filter((id) => {
        const node = ctx.nodeOf(id);
        return !!node && kinds.board(node.kind).element === "drawing";
      });
      if (drawn.length) await actions.removeNodes(drawn);
    },
  };
}
